/**
 * Student-marketplace design system for the web app (same tokens as the mobile apps):
 * warm paper background, navy primary, gold accent, 16px cards, 52px primary buttons.
 */
import React from 'react';
import { Box, Button, ButtonProps, CircularProgress, Typography } from '@mui/material';
import type { TenantQuote } from '../../services/quoteService';

export const DISPLAY = "'Bricolage Grotesque', Georgia, serif";
export const BODY = "'Figtree', system-ui, sans-serif";

export const M = {
  bg: '#F6F4EE', navy: '#002B5B', navyDark: '#00437F', gold: '#C9A84C', goldSoft: '#FBF6E6',
  text: '#14213D', text2: '#4A5468', text3: '#3A4458', border: '#E4DFD3', borderStrong: '#D9D4C7',
  green: '#1F5A3A', greenSoft: '#E6F1EA', amber: '#8A5A00', amberSoft: '#FFF1D6', red: '#B3261E',
  tintA: '#CFD8E3', tintB: '#DCD5C9', sage: '#E6F0EA', rose: '#8A2324', sand: '#EAE5D8', navTint: '#F1E6C4',
};

export const cedis = (n: number | null | undefined, d = 0) =>
  n == null || !Number.isFinite(n) ? '—' : `GH₵${n.toLocaleString('en-GH', { minimumFractionDigits: d, maximumFractionDigits: d })}`;

export function freshness(iso?: string | null): string | null {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (!Number.isFinite(days) || days < 0) return null;
  return days === 0 ? 'Availability confirmed today' : `Availability confirmed ${days} day${days === 1 ? '' : 's'} ago`;
}

export interface RoomOption { key: string; label: string; rent: number; listedPrice: number; studentFee: number; totalAmount: number; available: boolean }

export function fromPrice(p: any): { amount: number | null; freeBeds: boolean } {
  const opts: RoomOption[] = Array.isArray(p?.roomOptions) ? p.roomOptions : [];
  const open = opts.filter((o) => o.available);
  const pool = open.length ? open : opts;
  if (pool.length) return { amount: Math.min(...pool.map((o) => o.totalAmount)), freeBeds: open.length > 0 };
  return { amount: typeof p?.listedPrice === 'number' ? p.listedPrice + (p.studentFee ?? 0) : null, freeBeds: p?.available !== false };
}

export function walkLabel(p: any): string | null {
  const n = parseInt(String(p?.walkTime ?? ''), 10);
  if (Number.isFinite(n) && n > 0) return `${n} min walk`;
  const km = Number(p?.distanceToUniversity);
  return Number.isFinite(km) && km > 0 ? `${Math.max(1, Math.round(km * 12))} min walk` : null;
}

export const areaLabel = (p: any) => p?.constituency || p?.city || p?.address || '';

export const Pill: React.FC<{ label: string; tone?: 'green' | 'amber' | 'navy' | 'gold' }> = ({ label, tone = 'green' }) => {
  const c = { green: [M.greenSoft, M.green], amber: [M.amberSoft, M.amber], navy: [M.navy, '#fff'], gold: [M.goldSoft, M.navy] }[tone];
  return <Box component="span" sx={{ display: 'inline-block', px: 1.25, py: 0.4, borderRadius: '12px', bgcolor: c[0], color: c[1], fontSize: 12, fontWeight: 700 }}>{label}</Box>;
};

export const Card: React.FC<{ children: React.ReactNode; sx?: any; onClick?: () => void }> = ({ children, sx, onClick }) => (
  <Box onClick={onClick} sx={{ bgcolor: '#fff', border: `1px solid ${M.border}`, borderRadius: '16px', ...(onClick ? { cursor: 'pointer', '&:hover': { borderColor: M.borderStrong } } : {}), ...sx }}>{children}</Box>
);

export const SectionTitle: React.FC<{ title: string; action?: string; onAction?: () => void }> = ({ title, action, onAction }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1.5 }}>
    <Typography component="h2" sx={{ fontFamily: DISPLAY, fontSize: 24, fontWeight: 800, color: M.navy }}>{title}</Typography>
    {action && <Button onClick={onAction} sx={{ textTransform: 'none', fontWeight: 700, color: M.navy }}>{action}</Button>}
  </Box>
);

export const PrimaryButton: React.FC<ButtonProps & { busy?: boolean; kind?: 'primary' | 'outline' | 'gold' }> = ({ busy, kind = 'primary', children, sx, disabled, ...rest }) => (
  <Button
    {...rest} disabled={disabled || busy}
    sx={{
      minHeight: 52, borderRadius: '14px', textTransform: 'none', fontWeight: 800, fontSize: 16, px: 3,
      ...(kind === 'primary' && { bgcolor: M.navy, color: '#fff', '&:hover': { bgcolor: M.navyDark } }),
      ...(kind === 'gold' && { bgcolor: M.gold, color: M.navy, '&:hover': { bgcolor: '#B8993F' } }),
      ...(kind === 'outline' && { bgcolor: '#fff', color: M.navy, border: `1.5px solid ${M.navy}`, '&:hover': { bgcolor: M.goldSoft } }),
      ...sx,
    }}
  >{busy ? <CircularProgress size={22} sx={{ color: kind === 'outline' ? M.navy : '#fff' }} /> : children}</Button>
);

export const FilterChip: React.FC<{ label: string; on?: boolean; onClick?: () => void }> = ({ label, on, onClick }) => (
  <Button
    onClick={onClick} aria-pressed={!!on}
    sx={{ minHeight: 40, borderRadius: '20px', textTransform: 'none', fontSize: 14, whiteSpace: 'nowrap', px: 2, flexShrink: 0,
      ...(on ? { bgcolor: M.navy, color: '#fff', fontWeight: 700, border: `1px solid ${M.navy}`, '&:hover': { bgcolor: M.navyDark } } : { bgcolor: '#fff', color: M.text, fontWeight: 500, border: `1px solid ${M.borderStrong}` }) }}
  >{label}</Button>
);

/** Pesewas to cedis, showing pesewas only when they are not zero. */
export const cedisFromPesewas = (p: number | null | undefined) =>
  p == null || !Number.isFinite(p) ? '—' : cedis(p / 100, p % 100 === 0 ? 0 : 2);

/**
 * The one price list. Give it the tenant side of a server quote and it shows rent, "Booking and digital lease"
 * (only when there is one), payment processing and the total. It never shows commission. The older `lines` +
 * `total` form is kept for screens that are not on quotes yet.
 */
export const PriceBreakdown: React.FC<{
  quote?: TenantQuote | null; lines?: { label: string; amount: number }[]; total?: number;
  rentLabel?: string; totalLabel?: string; decimals?: number; bare?: boolean;
}> = ({ quote, lines, total, rentLabel = 'Rent', totalLabel = 'You pay, nothing more', decimals = 2, bare }) => {
  const rows: { label: string; text: string }[] = quote
    ? [
      { label: rentLabel, text: cedisFromPesewas(quote.rentPesewas) },
      ...(quote.studentFeePesewas > 0 ? [{ label: 'Booking and digital lease', text: cedisFromPesewas(quote.studentFeePesewas) }] : []),
      { label: 'Payment processing', text: cedisFromPesewas(quote.processingPesewas) },
    ]
    : (lines ?? []).map((l) => ({ label: l.label, text: cedis(l.amount, decimals) }));
  const totalText = quote ? cedisFromPesewas(quote.totalPesewas) : cedis(total, decimals);
  const body = (
    <>
      {rows.map((l) => (
        <Box key={l.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography sx={{ fontSize: 15, color: M.text }}>{l.label}</Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: M.text }}>{l.text}</Typography>
        </Box>
      ))}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: `1px solid ${M.border}` }}>
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: M.navy }}>{totalLabel}</Typography>
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: M.navy }}>{totalText}</Typography>
      </Box>
    </>
  );
  return bare ? <Box sx={{ display: 'grid', gap: 1 }}>{body}</Box> : <Card sx={{ p: 2, display: 'grid', gap: 1 }}>{body}</Card>;
};

export const Photo: React.FC<{ src?: string | null; height: number | string; tint?: string; children?: React.ReactNode; alt?: string }> = ({ src, height, tint = M.tintA, children, alt = '' }) => (
  <Box sx={{ position: 'relative', height, bgcolor: tint, overflow: 'hidden' }}>
    {src && <Box component="img" src={src} alt={alt} loading="lazy" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
    {children}
  </Box>
);

// ── Homes helpers ────────────────────────────────────────────────────────────

/** What one rent payment covers, from the landlord's chosen schedule (monthly when none was set). */
export function schedule(s?: string | null): { per: string; months: number; every: string } {
  switch (s) {
    case 'QUARTERLY': return { per: 'quarter', months: 3, every: 'every 3 months' };
    case 'SEMESTER': return { per: 'semester', months: 5, every: 'every semester' };
    case 'SIX_MONTHS': return { per: '6 months', months: 6, every: 'every 6 months' };
    case 'YEAR': return { per: 'year', months: 12, every: 'once a year' };
    default: return { per: 'month', months: 1, every: 'monthly' };
  }
}

/** All-in price: rent + Paystack processing (no student fee for homes); a sale is its asking price. */
export function allIn(p: any): number | null {
  if (p?.listingType === 'SALE') return typeof p.price === 'number' ? p.price : null;
  if (typeof p?.checkoutPricing?.totalAmount === 'number') return p.checkoutPricing.totalAmount;
  if (typeof p?.listedPrice === 'number') return p.listedPrice + (p.studentFee ?? 0);
  return typeof p?.price === 'number' ? p.price : null;
}

export const homeArea = (p: any) => [p?.constituency || p?.address, p?.city].filter(Boolean).join(', ');

export function facts(p: any): string {
  const parts: string[] = [];
  if (p?.bedrooms) parts.push(`${p.bedrooms} bd`);
  if (p?.bathrooms) parts.push(`${p.bathrooms} ba`);
  if (p?.squareFootage) parts.push(`${Number(p.squareFootage).toLocaleString()} sqft`);
  return parts.join(' · ');
}
